import { ref } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../../../consts";

const PAGE_SIZE = 20;

export function useSocialLibrary(token) {
  const toast = useToast();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const videos = ref([]);
  const totalVideos = ref(0);
  const currentPage = ref(1);
  const loading = ref(false);

  const podcasters = ref([]);
  const shows = ref([]);
  const sellerGroups = ref([]);

  const selectedPodcasterId = ref(null);
  const selectedShowId = ref(null);
  const selectedSellerGroupId = ref(null);
  const dateRange = ref(null);
  const minLength = ref(null);
  const maxLength = ref(null);
  const snippetIdFilter = ref(null);

  const currentlyPlayingId = ref(null);

  const transcriptDialogVisible = ref(false);
  const transcriptLoadingId = ref(null);
  const transcriptText = ref("");

  let latestRequestId = 0;

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/social-library/filter-options`,
        authHeaders
      );
      podcasters.value = response.data.data.podcasters;
      shows.value = response.data.data.shows;
      sellerGroups.value = response.data.data.sellerGroups;
    } catch (error) {
      console.error("Error fetching social library filter options:", error);
    }
  };

  const fetchVideos = async () => {
    const requestId = ++latestRequestId;
    try {
      loading.value = true;
      const [startDate, endDate] = dateRange.value || [];
      const response = await axios.get(
        `${API_BASE_URL}/social-library/videos`,
        {
          ...authHeaders,
          params: {
            podcasterId: selectedPodcasterId.value || undefined,
            showId: selectedShowId.value || undefined,
            sellerGroupId: selectedSellerGroupId.value || undefined,
            snippetId: snippetIdFilter.value || undefined,
            startDate: startDate
              ? startDate.toISOString().slice(0, 10)
              : undefined,
            endDate: endDate ? endDate.toISOString().slice(0, 10) : undefined,
            minDuration: minLength.value ?? undefined,
            maxDuration: maxLength.value ?? undefined,
            page: currentPage.value,
          },
        }
      );
      if (requestId !== latestRequestId) return;
      videos.value = response.data.data.videos.map((video) => ({
        ...video,
        metadata: video.metadata ? JSON.parse(video.metadata) : {},
      }));
      totalVideos.value = response.data.data.total;
    } catch (error) {
      if (requestId !== latestRequestId) return;
      console.error("Error fetching social library videos:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load videos.",
        life: 4000,
      });
    } finally {
      loading.value = false;
    }
  };

  const resetOtherFilters = (keep) => {
    if (keep !== "podcaster") selectedPodcasterId.value = null;
    if (keep !== "show") selectedShowId.value = null;
    if (keep !== "sellerGroup") selectedSellerGroupId.value = null;
    if (keep !== "period") dateRange.value = null;
    if (keep !== "length") {
      minLength.value = null;
      maxLength.value = null;
    }
  };

  const applyFilterAndRefetch = () => {
    currentPage.value = 1;
    fetchVideos();
  };

  const selectPodcaster = (podcasterId) => {
    resetOtherFilters("podcaster");
    selectedPodcasterId.value = podcasterId;
    applyFilterAndRefetch();
  };

  const selectShow = (showId) => {
    resetOtherFilters("show");
    selectedShowId.value = showId;
    applyFilterAndRefetch();
  };

  const selectSellerGroup = (sellerGroupId) => {
    resetOtherFilters("sellerGroup");
    selectedSellerGroupId.value = sellerGroupId;
    applyFilterAndRefetch();
  };

  const selectDateRange = (range) => {
    resetOtherFilters("period");
    dateRange.value = range;
    applyFilterAndRefetch();
  };

  const selectLength = (min, max) => {
    resetOtherFilters("length");
    minLength.value = min;
    maxLength.value = max;
    applyFilterAndRefetch();
  };

  const clearSnippetIdFilter = () => {
    snippetIdFilter.value = null;
    applyFilterAndRefetch();
  };

  const onPage = (event) => {
    currentPage.value = event.page + 1;
    fetchVideos();
  };

  const setPlaying = (assetId) => {
    currentlyPlayingId.value = assetId;
  };

  const fetchTranscript = async (video) => {
    transcriptLoadingId.value = video.asset_id;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/snippets/by-snippet-id/${video.snippet_id}/transcript`,
        authHeaders
      );
      transcriptText.value = response.data.data.transcript;
      transcriptDialogVisible.value = true;
    } catch (error) {
      console.error("Error fetching transcript:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load transcript.",
        life: 4000,
      });
    } finally {
      transcriptLoadingId.value = null;
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.add({
        severity: "success",
        summary: "Copied",
        detail: "Link copied to clipboard.",
        life: 3000,
      });
    } catch (error) {
      console.error("Error copying link:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy link.",
        life: 4000,
      });
    }
  };

  return {
    videos,
    totalVideos,
    currentPage,
    loading,
    pageSize: PAGE_SIZE,
    podcasters,
    shows,
    sellerGroups,
    selectedPodcasterId,
    selectedShowId,
    selectedSellerGroupId,
    dateRange,
    minLength,
    maxLength,
    snippetIdFilter,
    currentlyPlayingId,
    transcriptDialogVisible,
    transcriptLoadingId,
    transcriptText,
    fetchFilterOptions,
    fetchVideos,
    fetchTranscript,
    selectPodcaster,
    selectShow,
    selectSellerGroup,
    selectDateRange,
    selectLength,
    clearSnippetIdFilter,
    onPage,
    setPlaying,
    copyLink,
  };
}
